/**
 * Background settings panel for banner editor — supports multiple cycling backgrounds
 */
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Upload, Trash2, ChevronLeft, ChevronRight, Plus, ImageIcon } from "lucide-react";

export function BackgroundSettings({ banner, setBanner, uploading, onUpload }) {
  const fileInputRef = useRef(null);
  const backgrounds = banner.background_images || [];
  const activeIndex = banner.background_index || 0;
  const hasMultiple = backgrounds.length > 1;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUpload(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const setActiveIndex = (idx) => {
    setBanner({ ...banner, background_index: idx, background_image: backgrounds[idx] || banner.background_image });
  };

  const removeBackground = (idx) => {
    const newBgs = [...backgrounds];
    newBgs.splice(idx, 1);
    let newIndex = activeIndex;
    if (newIndex >= newBgs.length) newIndex = Math.max(0, newBgs.length - 1);
    setBanner({
      ...banner,
      background_images: newBgs,
      background_index: newIndex,
      background_image: newBgs[newIndex] || "",
    });
  };

  const moveBackground = (idx, direction) => {
    const newBgs = [...backgrounds];
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= newBgs.length) return;
    [newBgs[idx], newBgs[targetIdx]] = [newBgs[targetIdx], newBgs[idx]];
    let newIndex = activeIndex;
    if (activeIndex === idx) newIndex = targetIdx;
    else if (activeIndex === targetIdx) newIndex = idx;
    setBanner({
      ...banner,
      background_images: newBgs,
      background_index: newIndex,
      background_image: newBgs[newIndex] || banner.background_image,
    });
  };

  return (
    <div>
      <Label className="text-xs text-gray-400 uppercase tracking-wider" data-testid="background-label">
        Background
      </Label>

      {/* Transparent background toggle */}
      <div className="flex items-center justify-between mt-2 mb-2">
        <span className="text-xs text-gray-500">Transparent</span>
        <Switch
          checked={banner.use_transparent_background}
          onCheckedChange={(checked) => setBanner({ ...banner, use_transparent_background: checked })}
          data-testid="transparent-bg-toggle"
          className="scale-75"
        />
      </div>

      {!banner.use_transparent_background && (
        <>
          {/* Background thumbnails */}
          {backgrounds.length > 0 && (
            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Background {activeIndex + 1} of {backgrounds.length}
                  {hasMultiple && <span className="text-[#10B981] ml-1">(cycles per draw)</span>}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {backgrounds.map((bg, idx) => (
                  <div
                    key={`bg-${idx}`}
                    className={`relative group rounded overflow-hidden cursor-pointer border-2 transition-all ${
                      idx === activeIndex
                        ? "border-[#10B981] shadow-md shadow-[#10B981]/20"
                        : "border-transparent hover:border-white/20"
                    }`}
                    onClick={() => setActiveIndex(idx)}
                    data-testid={`bg-thumb-${idx}`}
                  >
                    <img
                      src={bg}
                      alt={`Background ${idx + 1}`}
                      className="w-full aspect-video object-cover"
                    />
                    {idx === activeIndex && (
                      <div className="absolute top-0.5 left-0.5 bg-[#10B981] text-white text-[9px] font-bold px-1 rounded">
                        ACTIVE
                      </div>
                    )}
                    {/* Overlay controls on hover */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                      {idx > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); moveBackground(idx, -1); }}
                          className="p-1 rounded bg-white/10 hover:bg-white/20"
                          title="Move left"
                        >
                          <ChevronLeft className="w-3 h-3 text-white" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); removeBackground(idx); }}
                        className="p-1 rounded bg-red-500/30 hover:bg-red-500/50"
                        title="Remove"
                        data-testid={`bg-remove-${idx}`}
                      >
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </button>
                      {idx < backgrounds.length - 1 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); moveBackground(idx, 1); }}
                          className="p-1 rounded bg-white/10 hover:bg-white/20"
                          title="Move right"
                        >
                          <ChevronRight className="w-3 h-3 text-white" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload button */}
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-background-button"
            variant="outline"
            className="w-full h-9 border-dashed border-white/20 text-gray-400 hover:text-white hover:border-[#10B981] hover:bg-[#10B981]/10"
          >
            {backgrounds.length > 0 ? (
              <><Plus className="w-4 h-4 mr-2" />{uploading ? "Uploading..." : "Add Background"}</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" />{uploading ? "Uploading..." : "Upload Image"}</>
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />

          {backgrounds.length === 0 && banner.background_image && (
            <p className="text-xs text-[#10B981] mt-1">1 background uploaded</p>
          )}

          {hasMultiple && (
            <p className="text-xs text-gray-500 mt-1.5">
              Backgrounds cycle automatically each time results update.
            </p>
          )}

          {/* Background color fallback */}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-500">Or solid color:</span>
            <input
              type="color"
              value={banner.background_color || "#ffffff"}
              onChange={(e) => setBanner({ ...banner, background_color: e.target.value })}
              className="w-6 h-6 rounded cursor-pointer border-0"
            />
          </div>
        </>
      )}

      {banner.use_transparent_background && (
        <p className="text-xs text-amber-500 mt-1">PNG downloads will have transparent background</p>
      )}
    </div>
  );
}
